import React, { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Box, Typography } from '@mui/material'

import { CollapsibleTable, TruncatedObservation, ObservationsDialog } from '../../../../ui'
import { useCombos } from '../../../combos/hooks'
import { useAppsCalls } from '../../hooks'
import {
  renderLoading,
  findComboItem,
  findEstadoByKey,
  formatTaskDate,
} from '../../../../core/utils'
import { APP_TASK_TABLE_CONFIG } from '../../constants'

const REQUIRED_COMBOS = ['tipologias_tareas', 'estados_sspp', 'subtipologias_tareas']

const ESTADOS_PETICIONARIO_MAP = {
  'Pdte. de terceros': 'En Gestión',
  Asignada: 'Creada',
}

const mapEstadoForPeticionario = (estado) => {
  if (!estado) return null
  return ESTADOS_PETICIONARIO_MAP[estado] || estado
}

export const AppTaskTable = ({ application }) => {
  const intl = useIntl()
  const combos = useCombos(REQUIRED_COMBOS)
  const { getAppTasksCall } = useAppsCalls()
  const role = useSelector((state) => state.global.role)
  const permissions = useSelector((state) => state.global.permissions) || {}

  const [loading, setLoading] = useState(true)
  const [tasks, setTasks] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedObservations, setSelectedObservations] = useState('')

  const canViewInternalStates = useMemo(() => {
    return false
  }, [role, permissions])

  const handleOpenObservations = (observaciones) => {
    setSelectedObservations(observaciones || '')
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedObservations('')
  }

  const tasksData = tasks || []

  const processedTasks = useMemo(() => {
    if (!tasksData?.length || !combos) return []

    if (!combos.tipologias_tareas || !combos.estados_sspp || !combos.subtipologias_tareas) {
      return []
    }

    return tasksData.map((task, index) => {
      const tipologia = findComboItem(combos.tipologias_tareas, task.tipologia_id)
      const estado = findEstadoByKey(combos.estados_sspp, task.estado)
      const documentoCampo = findComboItem(combos.subtipologias_tareas, task.tipologia_id)
      const fechaFormateada = formatTaskDate(task.dg_ts_insert, intl)

      let estadoNombre = estado ? estado.value : `Estado ${task.estado}`
      if (!canViewInternalStates && estado?.value) {
        estadoNombre = mapEstadoForPeticionario(estado.value)
      }

      return {
        ...task,
        id: task.id || `row-${index}`,
        tipologia_nombre: tipologia ? tipologia.value : `Tipología ${task.tipologia_id}`,
        estado_nombre: estadoNombre,
        documento_campo: documentoCampo ? documentoCampo.value : '--',
        dg_ts_insert_formatted: fechaFormateada,
        observaciones: (
          <TruncatedObservation
            text={task.observaciones_sspp || '--'}
            onClick={() => handleOpenObservations(task.observaciones_sspp)}
          />
        ),
      }
    })
  }, [tasksData, combos, intl, canViewInternalStates])

  useEffect(() => {
    let isMounted = true

    if (application?.id) {
      setLoading(true)
      getAppTasksCall(application.id)
        .then((response) => {
          if (isMounted) {
            setTasks(response.data || [])
            setLoading(false)
          }
        })
        .catch((error) => {
          if (isMounted) {
            setTasks([])
            setLoading(false)
          }
        })
    } else {
      if (isMounted) {
        setLoading(false)
        setTasks([])
      }
    }

    return () => {
      isMounted = false
    }
  }, [application?.id, getAppTasksCall])

  const tableConfig = useMemo(() => {
    try {
      return APP_TASK_TABLE_CONFIG ? APP_TASK_TABLE_CONFIG(intl) : []
    } catch (error) {
      console.error('Error creating table config:', error)
      return []
    }
  }, [intl])

  if (loading) {
    return renderLoading()
  }

  if (!processedTasks || processedTasks.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
        minHeight="60px"
        bgcolor="transparent"
        borderRadius={1}
        py={1}
        px={3}
      >
        <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
          {intl.formatMessage({ id: 'pages.application.detail.history.noTasks' })}
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <CollapsibleTable config={tableConfig} data={processedTasks} />

      <ObservationsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        observaciones={selectedObservations}
      />
    </>
  )
}
